import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Ticket, 
  Plus, 
  Trash2,
  X,
  Check,
  Calendar,
  Percent
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

const CouponListScreen = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderAmount: '',
    expiryDate: ''
  });

  const { getConfig } = useAuth();

  const fetchCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/coupons`, getConfig());
      setCoupons(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setLoading(false);
    }
  }, [getConfig]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleOpenModal = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderAmount: '',
      expiryDate: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        discountValue: Number(formData.discountValue),
        minOrderAmount: formData.minOrderAmount ? Number(formData.minOrderAmount) : 0,
        expiryDate: formData.expiryDate || null
      };

      await axios.post(`${API_URL}/coupons`, payload, getConfig());
      
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      alert(error.response?.data?.message || 'Error creating promo code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this promo code?')) {
      try {
        await axios.delete(`${API_URL}/coupons/${id}`, getConfig());
        fetchCoupons();
      } catch (error) {
        alert('Error deleting promo code');
      }
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Promo Codes</h1>
          <p className="text-secondary-500 font-medium">Manage discount codes and promotional offers.</p>
        </div>
        <button 
          onClick={handleOpenModal}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-primary-200 flex items-center space-x-2 transition-all"
        >
          <Plus className="h-5 w-5" />
          <span>Create Promo Code</span>
        </button>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-[32px] border border-secondary-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary-50/50 text-left border-b border-secondary-100">
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Code</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Discount</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Min Order</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Expiry Date</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Status</th>
                <th className="px-8 py-5 text-right font-black text-secondary-400 text-[10px] uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-50">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-8 py-6 h-20 bg-secondary-50/20" />
                  </tr>
                ))
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-20">
                    <div className="space-y-4 max-w-sm mx-auto">
                      <Ticket className="h-12 w-12 text-secondary-200 mx-auto" />
                      <p className="text-secondary-500 font-bold">No promo codes found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => {
                  const isExpired = coupon.expiryDate && new Date(coupon.expiryDate) < new Date();
                  const statusLabel = isExpired ? 'Expired' : (coupon.isActive ? 'Active' : 'Inactive');
                  const statusColor = isExpired ? 'bg-red-100 text-red-700' : (coupon.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-secondary-100 text-secondary-600');
                  
                  return (
                    <tr key={coupon._id} className="hover:bg-secondary-50/50 transition-colors group font-medium">
                      <td className="px-8 py-5">
                        <span className="font-mono font-bold text-secondary-900 bg-secondary-100 px-3 py-1.5 rounded-lg border border-secondary-200">
                          {coupon.code}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-secondary-900 font-bold">
                        {coupon.discountType === 'percentage' ? (
                          <span className="flex items-center space-x-1">
                            <span>{coupon.discountValue}</span>
                            <Percent className="h-4 w-4 text-secondary-500" />
                          </span>
                        ) : (
                          <span>₹{coupon.discountValue} Off</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-secondary-700 font-semibold">
                        ₹{coupon.minOrderAmount || 0}
                      </td>
                      <td className="px-6 py-5 text-secondary-500">
                        {coupon.expiryDate ? (
                          <span className="flex items-center space-x-1.5 text-xs">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{new Date(coupon.expiryDate).toLocaleDateString()}</span>
                          </span>
                        ) : (
                          <span className="text-xs italic text-secondary-400">Never Expires</span>
                        )}
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <button 
                          onClick={() => handleDelete(coupon._id)}
                          className="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Coupon Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-lg my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 md:p-10 space-y-8">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tighter">
                    Create Promo Code
                  </h2>
                  <p className="text-secondary-500 font-medium">Add a new coupon offer for your customers.</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-3 hover:bg-secondary-100 rounded-2xl transition-all">
                  <X className="h-6 w-6 text-secondary-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Coupon Code</label>
                  <input 
                    required
                    type="text" 
                    placeholder="e.g. SUMMER50"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 uppercase font-mono"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Discount Type</label>
                    <select
                      value={formData.discountType}
                      onChange={(e) => setFormData({...formData, discountType: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 appearance-none"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Discount Value</label>
                    <input 
                      required
                      type="number" 
                      placeholder={formData.discountType === 'percentage' ? '10%' : '200'}
                      value={formData.discountValue}
                      onChange={(e) => setFormData({...formData, discountValue: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Min Order Amount (₹)</label>
                    <input 
                      type="number" 
                      placeholder="0"
                      value={formData.minOrderAmount}
                      onChange={(e) => setFormData({...formData, minOrderAmount: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Expiry Date</label>
                    <input 
                      type="date" 
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                    />
                  </div>
                </div>

                <div className="pt-4 flex space-x-4">
                  <button 
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-4 rounded-2xl font-bold text-secondary-500 hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    disabled={isSubmitting}
                    className="flex-[2] bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white py-4 rounded-2xl font-bold uppercase transition-all flex items-center justify-center space-x-2 shadow-lg shadow-primary-100"
                  >
                    {isSubmitting ? (
                       <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Check className="h-5 w-5" />
                        <span>Create Promo Code</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponListScreen;
